import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getEnhancedECMRList, loadExampleECMRs } from '../services/eCMRService';
import AuthStatusBanner from './AuthStatusBanner';

const ECMRList: React.FC = () => {
  const [ecmrList, setEcmrList] = useState<{ id: string, title: string, date: string, source: 'local' | 'api' }[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingExamples, setLoadingExamples] = useState<boolean>(false);
  const [exampleMessage, setExampleMessage] = useState<{ show: boolean, type: 'success' | 'error', message: string }>({
    show: false,
    type: 'success',
    message: ''
  });

  useEffect(() => {
    loadECMRList();
  }, []);

  const loadECMRList = async () => {
    setLoading(true);
    try {
      const list = await getEnhancedECMRList();
      setEcmrList(list);
    } catch (error) {
      console.error('Error loading ECMR list:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadExamples = () => {
    setLoadingExamples(true);
    try {
      const savedKeys = loadExampleECMRs();
      if (savedKeys.length > 0) {
        setExampleMessage({
          show: true,
          type: 'success',
          message: `Se han cargado ${savedKeys.length} documentos de ejemplo correctamente.`
        });
        
        // Ocultar el mensaje después de 5 segundos
        setTimeout(() => {
          setExampleMessage(prev => ({ ...prev, show: false }));
        }, 5000);
        
        // Refrescar la lista
        loadECMRList();
      } else {
        setExampleMessage({
          show: true,
          type: 'error',
          message: 'No se pudo cargar ningún documento de ejemplo.'
        });
      }
    } catch (error) {
      setExampleMessage({
        show: true,
        type: 'error',
        message: `Error al cargar ejemplos: ${(error as Error).message}`
      });
    } finally {
      setLoadingExamples(false);
    }
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return 'Fecha desconocida';
    
    // Format: YYYYMMDDHHMMSS
    if (dateStr.length === 14) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      const hour = dateStr.substring(8, 10);
      const minute = dateStr.substring(10, 12);
      
      return `${day}/${month}/${year} ${hour}:${minute}`;
    }
    
    return dateStr;
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Documentos e-CMR</h1>
      
      <AuthStatusBanner />
      
      {exampleMessage.show && (
        <div className={`mb-4 p-4 rounded ${exampleMessage.type === 'success' ? 'bg-green-100 border-l-4 border-green-500 text-green-700' : 'bg-red-100 border-l-4 border-red-500 text-red-700'}`}>
          <p>{exampleMessage.message}</p>
        </div>
      )}
      
      <div className="mb-6 flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <Link to="/create" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-full sm:w-auto text-center">
            <i className="fas fa-plus mr-2"></i> Crear Nuevo
          </Link>
          <Link to="/import" className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded w-full sm:w-auto text-center">
            <i className="fas fa-file-import mr-2"></i> Importar XML
          </Link>
          <button
            onClick={handleLoadExamples}
            disabled={loadingExamples}
            className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded w-full sm:w-auto text-center"
          >
            <i className="fas fa-flask mr-2"></i> 
            {loadingExamples ? 'Cargando ejemplos...' : 'Cargar Ejemplos'}
          </button>
        </div>
        
        <button 
          onClick={loadECMRList} 
          className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded w-full sm:w-auto"
          disabled={loading}
        >
          <i className="fas fa-sync-alt mr-2"></i> Actualizar
        </button>
      </div>
      
      {loading ? (
        <div className="bg-white shadow-md rounded p-6 text-center">
          <p className="text-lg">Cargando documentos...</p>
        </div>
      ) : ecmrList.length === 0 ? (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
          <h3 className="font-bold">No hay documentos e-CMR guardados</h3>
          <p>Para comenzar, crea un nuevo e-CMR, importa un documento XML existente o carga ejemplos de demostración.</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Link to="/create" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-center">
              <i className="fas fa-plus mr-2"></i> Crear mi primer e-CMR
            </Link>
            <button
              onClick={handleLoadExamples}
              className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded text-center"
            >
              <i className="fas fa-flask mr-2"></i> Cargar Ejemplos de Demostración
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Título
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Origen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ecmrList.map((ecmr) => (
                  <tr key={ecmr.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {ecmr.id.replace('ecmr_', '')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {ecmr.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(ecmr.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        ecmr.source === 'api' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {ecmr.source === 'api' ? '☁️ Nube' : '💾 Local'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-wrap gap-2">
                        <Link 
                          to={`/view/${ecmr.id}`} 
                          className="text-blue-600 hover:text-blue-900 bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded"
                          title="Ver documento"
                        >
                          <i className="fas fa-eye mr-1"></i> Ver
                        </Link>
                        <Link 
                          to={`/edit/${ecmr.id}`} 
                          className="text-green-600 hover:text-green-900 bg-green-100 hover:bg-green-200 px-2 py-1 rounded"
                          title="Editar documento"
                        >
                          <i className="fas fa-edit mr-1"></i> Editar
                        </Link>
                        <Link 
                          to={`/export/${ecmr.id}`} 
                          className="text-purple-600 hover:text-purple-900 bg-purple-100 hover:bg-purple-200 px-2 py-1 rounded"
                          title="Exportar a XML"
                        >
                          <i className="fas fa-file-export mr-1"></i> Exportar
                        </Link>
                        <Link 
                          to={`/validate/${ecmr.id}`} 
                          className="text-yellow-600 hover:text-yellow-900 bg-yellow-100 hover:bg-yellow-200 px-2 py-1 rounded"
                          title="Validar contra el estándar UN/CEFACT"
                        >
                          <i className="fas fa-check-circle mr-1"></i> Validar
                        </Link>
                        <Link 
                          to={`/sign/${ecmr.id}`} 
                          className="text-indigo-600 hover:text-indigo-900 bg-indigo-100 hover:bg-indigo-200 px-2 py-1 rounded"
                          title="Firmar documento"
                        >
                          <i className="fas fa-signature mr-1"></i> Firmar
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 text-blue-700 p-4 rounded">
        <h3 className="font-bold text-lg mb-2">¿Qué es un e-CMR?</h3>
        <p className="mb-2">
          El e-CMR (carta de porte electrónica) es la versión digital del documento CMR utilizado en el transporte internacional de mercancías por carretera.
        </p>
        <p>
          Esta aplicación implementa el estándar UN/CEFACT para la creación, edición y validación de documentos e-CMR conformes a la normativa internacional.
        </p>
      </div>
    </div>
  );
};

export default ECMRList;